import type { AnalyticsHubReportDefinition } from '../../../../../components/api/analyticsHubService';
import type { ReportDefinition, ReportSettings, ReportType, SourceType } from './types';
import { combineTemplateParts, type TemplateParts } from './templateParts';

const ICON_ALIASES: Record<string, string> = {
  BarChart3: 'barChart',
  PieChart: 'pieChart',
  LineChart: 'trendUp',
  FileText: 'fileText',
  FileBarChart: 'fileText',
  TrendingUp: 'trendUp',
  Users: 'users',
  MessageSquare: 'message',
  Table: 'fileText',
  ClipboardList: 'fileText',
  Activity: 'trendUp',
  Database: 'fileText',
};

function normalizeReportIcon(icon?: string): string {
  if (!icon) return 'barChart';
  if (ICON_ALIASES[icon]) return ICON_ALIASES[icon];
  if (ICON_ALIASES[icon.charAt(0).toUpperCase() + icon.slice(1)]) {
    return ICON_ALIASES[icon.charAt(0).toUpperCase() + icon.slice(1)];
  }
  return icon in ICON_ALIASES ? icon : 'barChart';
}

function buildHtmlTemplate(api: AnalyticsHubReportDefinition): string | undefined {
  const finalHtml = api.final_report?.html;
  if (typeof finalHtml === 'string' && finalHtml.trim()) {
    return finalHtml;
  }

  if (api.jsx || api.css || api.js) {
    return combineTemplateParts({
      html: api.jsx ?? '',
      css: api.css ?? '',
      js: api.js ?? '',
    });
  }

  return undefined;
}

function buildReportSettings(
  api: AnalyticsHubReportDefinition,
  reportDbId: string
): ReportSettings {
  const sourceType = (api.source_type === 'datamodel' ? 'datamodel' : 'workflow') as SourceType;
  const reportType = (api.report_type === 'chatbot' ? 'chatbot' : 'standalone') as ReportType;

  return {
    reportId: api.report_id ?? reportDbId,
    isUniversalId: false,
    reportType,
    description: api.description ?? '',
    icon: api.icon ?? 'BarChart3',
    isActive: true,
    sourceType,
    workflowId: sourceType === 'workflow' ? (api.source_id ?? '') : '',
    dataModelId: sourceType === 'datamodel' ? (api.source_id ?? '') : '',
    roleAccessList: api.roles ?? [],
    userAccessList: api.users ?? [],
    deniedUsers: api.denied ?? [],
    allowedDepartmentIds: api.allowed_departments ?? [],
    copilotIds: api.copilot_ids ?? [],
    reportHeader: (api.report_header as Record<string, unknown>) ?? { title: '', subtitle: '' },
  };
}

export function templatePartsFromAnalyticsHubReport(
  api: AnalyticsHubReportDefinition
): TemplateParts {
  return {
    html: api.jsx ?? '',
    css: api.css ?? '',
    js: api.js ?? '',
  };
}

export function resolveAnalyticsHubCollectionId(
  api: AnalyticsHubReportDefinition,
  fallbackCollectionId: string
): string {
  return (
    api.collection_id ??
    api.analytics_collection_id ??
    api.collectionid ??
    fallbackCollectionId
  );
}

/** MongoDB document id from Analytics Hub (`_id` field). */
export function resolveReportDbId(api: AnalyticsHubReportDefinition): string {
  return (api._id ?? api.reportid ?? '').trim();
}

export function mapAnalyticsHubReportDefinition(
  api: AnalyticsHubReportDefinition,
  collectionId: string
): ReportDefinition {
  const id = resolveReportDbId(api);
  const htmlTemplate = buildHtmlTemplate(api);
  const hasContent = !!htmlTemplate;
  const resolvedCollectionId = resolveAnalyticsHubCollectionId(api, collectionId);

  return {
    id,
    name: api.report_name,
    description: api.description ?? '',
    category: api.report_type ?? 'Custom',
    icon: normalizeReportIcon(api.icon),
    collectionId: resolvedCollectionId,
    lastGenerated: api.updated_time ?? api.created_time,
    status: hasContent ? 'ready' : 'draft',
    htmlTemplate,
    settings: buildReportSettings(api, id),
  };
}
